/**
 * Skin Profile trait radar geometry — SSD-6042 (Figma 4338:2380, chart box 4338:2465).
 *
 * Web and mobile draw the same radar from the same `me/overview` payload, so every number that must
 * agree between them lives here: where each axis points, where its vertex, spoke end and label sit,
 * and which ring the polygon closes on. Colours, fonts and Tailwind classes stay in each app.
 *
 * Everything is expressed in the design's 288×284 coordinate space. Consumers scale that box to the
 * width they actually have; nothing is rounded here, so a consumer rounds only when it writes px.
 */
import { clamp } from 'es-toolkit';
import type {
  SkinAnalysisMetricRelativeStatus,
  SkinAnalysisOverviewTrait,
} from '../../model/skinAnalysis/skinAnalysis';
import { traitBucket } from './skinDiaryPresentation';

/** The design's chart box; consumers scale it proportionally below 288px of available width. */
export const TRAIT_RADAR_BOX = { width: 288, height: 284 } as const;
export const TRAIT_RADAR_CENTER = { x: 144, y: 137 } as const;

/** A score of 100 sits on this ring. */
export const TRAIT_RADAR_OUTER_RADIUS_PX = 86;
/** Spokes overshoot the outer ring by 4px, as the frame draws them. */
export const TRAIT_RADAR_SPOKE_RADIUS_PX = 90;
/** Radial glow behind everything else. */
export const TRAIT_RADAR_GLOW_RADIUS_PX = 136;
/** Outer ring first (solid), then the two inner rings (dashed). */
export const TRAIT_RADAR_RING_RADII_PX = [86, 57.333, 28.667] as const;
export const TRAIT_RADAR_RING_DASH = [3, 4] as const;
/** Distance from the centre to the centre of a label box. */
export const TRAIT_RADAR_LABEL_RADIUS_PX = 122;
/** Long names ("Under Eye Puffiness") wrap rather than widen past this. */
export const TRAIT_RADAR_LABEL_MAX_WIDTH_PX = 74;
const SIDE_LABEL_CLEARANCE_PX = 4;
/** QA: keep the two longest side labels clear of the plotted vertices. */
export function traitRadarLabelOffsetX(metricKey: string): number {
  if (metricKey === 'puffiness') return -SIDE_LABEL_CLEARANCE_PX;
  if (metricKey === 'pigmentation') return SIDE_LABEL_CLEARANCE_PX;
  return 0;
}

/** Presentation input also supports the locked illustration's Hydration axis. */
export type TraitRadarInput = Omit<SkinAnalysisOverviewTrait, 'metricKey'> & { metricKey: string };

/** Exact exported bounds of Figma 4410:2358, including its stroke. */
export const LOCKED_TRAIT_RADAR_ARTWORK = { x: 90.49, y: 80.82, width: 116.275, height: 119.334 } as const;

/** Locked labels only. Zero is never displayed or plotted; the shape is a Figma asset. */
export const LOCKED_TRAIT_RADAR_TRAITS: readonly TraitRadarInput[] = [
  { metricKey: 'breakouts', label: 'Breakouts', score: 0, relativeStatus: null },
  { metricKey: 'pores', label: 'Pores', score: 0, relativeStatus: null },
  { metricKey: 'redness', label: 'Redness', score: 0, relativeStatus: null },
  { metricKey: 'pigmentation', label: 'Pigmentation', score: 0, relativeStatus: null },
  { metricKey: 'lines', label: 'Lines', score: 0, relativeStatus: null },
  { metricKey: 'sagging', label: 'Sagging', score: 0, relativeStatus: null },
  { metricKey: 'uniformness', label: 'Uniformness', score: 0, relativeStatus: null },
  { metricKey: 'dark_circles', label: 'Dark Circles', score: 0, relativeStatus: null },
  { metricKey: 'puffiness', label: 'Under Eye Puffiness', score: 0, relativeStatus: null },
  { metricKey: 'hydration', label: 'Hydration', score: 0, relativeStatus: null },
];
/** Below three axes there is no area to shade, so the chart shows dots and labels only. */
export const TRAIT_RADAR_MIN_POLYGON_TRAITS = 3;
/** 8px dot including its 1px white ring. */
export const TRAIT_RADAR_DOT_RADIUS_PX = 3.5;

/** Scores are a 0–100 scale; anything outside it is a server anomaly, not a longer spoke. */
const MAX_SCORE = 100;
const FULL_TURN_RADIANS = Math.PI * 2;
const LABEL_HALF_WIDTH_PX = TRAIT_RADAR_LABEL_MAX_WIDTH_PX / 2;

export interface TraitRadarPoint {
  readonly x: number;
  readonly y: number;
}

/** Label centres from Figma 4410:2369–2396; the locked illustration is deliberately asymmetric. */
const LOCKED_LABEL_ANCHORS: Readonly<Record<string, TraitRadarPoint>> = {
  breakouts: { x: 144.5, y: 17 },
  pores: { x: 214, y: 36 },
  redness: { x: 263.5, y: 98 },
  pigmentation: { x: 251, y: 188 },
  lines: { x: 213.5, y: 236 },
  sagging: { x: 144.5, y: 258 },
  uniformness: { x: 64, y: 236 },
  dark_circles: { x: 32, y: 174 },
  puffiness: { x: 27.5, y: 96 },
  hydration: { x: 68.5, y: 36 },
};

export function lockedTraitRadarLayout(): TraitRadarLayout {
  const layout = traitRadarLayout(LOCKED_TRAIT_RADAR_TRAITS);
  return {
    axes: layout.axes.map((axis) => ({ ...axis, labelAnchor: LOCKED_LABEL_ANCHORS[axis.metricKey] })),
    polygonPoints: null,
  };
}

export type TraitRadarLabelAlign = 'start' | 'center' | 'end';

export interface TraitRadarAxis {
  readonly metricKey: string;
  readonly label: string;
  /** The server's score, verbatim — only the vertex is clamped to the ring band. */
  readonly score: number;
  readonly bucket: SkinAnalysisMetricRelativeStatus;
  /** Radians, 0 at 12 o'clock, increasing clockwise. */
  readonly angle: number;
  /** Polygon vertex and dot centre. */
  readonly vertex: TraitRadarPoint;
  readonly spokeEnd: TraitRadarPoint;
  /** Centre of the label box. */
  readonly labelAnchor: TraitRadarPoint;
  readonly labelAlign: TraitRadarLabelAlign;
}

export interface TraitRadarLayout {
  readonly axes: readonly TraitRadarAxis[];
  /** Null when there are too few axes to enclose an area. */
  readonly polygonPoints: readonly TraitRadarPoint[] | null;
}

/** Polar coordinates in the design box: 0 radians points up, and the angle turns clockwise. */
export function polarToPoint(angle: number, radius: number): TraitRadarPoint {
  return {
    x: TRAIT_RADAR_CENTER.x + radius * Math.sin(angle),
    y: TRAIT_RADAR_CENTER.y - radius * Math.cos(angle),
  };
}

function vertexRadiusPx(score: number): number {
  return (clamp(score, 0, MAX_SCORE) / MAX_SCORE) * TRAIT_RADAR_OUTER_RADIUS_PX;
}

/**
 * How a label box sits against its anchor. A box centred on an anchor near the left or right edge
 * would spill outside the chart, so those two flush against the edge instead.
 */
function labelAlignFor(anchorX: number): TraitRadarLabelAlign {
  if (anchorX < LABEL_HALF_WIDTH_PX) return 'start';
  if (anchorX > TRAIT_RADAR_BOX.width - LABEL_HALF_WIDTH_PX) return 'end';
  return 'center';
}

function toAxis(trait: TraitRadarInput, index: number, axisCount: number): TraitRadarAxis {
  const angle = (index * FULL_TURN_RADIANS) / axisCount;
  const labelAnchor = polarToPoint(angle, TRAIT_RADAR_LABEL_RADIUS_PX);

  return {
    metricKey: trait.metricKey,
    label: trait.label,
    score: trait.score,
    bucket: traitBucket(trait.relativeStatus),
    angle,
    vertex: polarToPoint(angle, vertexRadiusPx(trait.score)),
    spokeEnd: polarToPoint(angle, TRAIT_RADAR_SPOKE_RADIUS_PX),
    labelAnchor,
    labelAlign: labelAlignFor(labelAnchor.x),
  };
}

/**
 * Lay the traits out around the radar, one evenly spaced axis each, in the catalog order the API
 * sends them — the chart reads as the same shape between visits only if that order never shifts.
 */
export function traitRadarLayout(traits: readonly TraitRadarInput[]): TraitRadarLayout {
  const axes = traits.map((trait, index) => toAxis(trait, index, traits.length));
  const enclosesArea = axes.length >= TRAIT_RADAR_MIN_POLYGON_TRAITS;

  return {
    axes,
    polygonPoints: enclosesArea ? axes.map((axis) => axis.vertex) : null,
  };
}
